/**
 * «Gastos» in «Análisis», besides where the money goes and the break-even (the owner, 2026-10-13, of
 * what the page could add: «let's build all of it»): each expense account against what it may spend
 * a month, what is lost and to what, what the payroll takes of the sales and of the profit, the
 * hours the till is open against the hours that sell, and whether a new person or a new fixed
 * expense fits in what the shop makes.
 *
 * Read from the books as «En qué se va» reads them (`estadoResultados`), so a figure here is the
 * one «Contabilidad» has. Pure, so the screen and its tests share one answer.
 */

import { ZONA_HORARIA, type Centavos } from '@abastece/shared';
import { ACCOUNTS, calcularPlanilla, type EstadoResultados, type LineaReporte, type PayrollRates, type SesionCaja } from '@abastece/core';
import type { Tramo } from './analisis.js';
import { costoDePago } from './planilla.js';

/** What is lost goes to these accounts: spoilt, expired, eaten at home, missing, never paid. */
export const CUENTAS_DE_PERDIDA: readonly string[] = [
  ACCOUNTS.MERMA,
  ACCOUNTS.VENCIDOS,
  ACCOUNTS.CONSUMO_INTERNO,
  ACCOUNTS.FALTANTE_INVENTARIO,
  ACCOUNTS.FALTANTE_REPARTO,
  ACCOUNTS.FALTANTE_CAJA,
  ACCOUNTS.FALTANTE_GENERAL,
  ACCOUNTS.TRANSFERENCIAS_PERDIDAS,
  ACCOUNTS.DIFERENCIAS_CONCILIACION,
  ACCOUNTS.FIADO_INCOBRABLE,
].map((a) => a.code);

/** The payroll's accounts: the wages, the shop's INSS and INATEC, and what accrues for the year. */
export const CUENTAS_DE_PLANILLA: readonly string[] = [
  ACCOUNTS.SUELDOS,
  ACCOUNTS.INSS_PATRONAL,
  ACCOUNTS.INATEC_GASTO,
  ACCOUNTS.AGUINALDO_GASTO,
  ACCOUNTS.VACACIONES_GASTO,
  ACCOUNTS.INDEMNIZACION_GASTO,
].map((a) => a.code);

// ---------------------------------------------------------------------------
// Presupuesto
// ---------------------------------------------------------------------------

export interface FilaPresupuesto {
  codigo: string;
  nombre: string;
  gastado: Centavos;
  presupuesto: Centavos;
  /** What is spent of it, in basis points: past 10,000 once it is passed. */
  parteBp: number;
}

/**
 * A month's expense accounts against what each may spend: every account with a budget, spent or
 * not, the nearest to its budget first; and what went to accounts without one.
 */
export function avanceDelPresupuesto(
  presupuestos: Readonly<Record<string, number>>,
  gastos: readonly LineaReporte[],
  nombreDe: (codigo: string) => string,
): { filas: FilaPresupuesto[]; gastado: Centavos; presupuesto: Centavos; sinPresupuesto: { monto: Centavos; cuentas: number } } {
  const porCodigo = new Map(gastos.map((l) => [l.codigo, l.monto]));
  const filas = Object.entries(presupuestos)
    .filter(([, monto]) => monto > 0)
    .map(([codigo, presupuesto]) => {
      const gastado = porCodigo.get(codigo) ?? 0;
      return { codigo, nombre: nombreDe(codigo), gastado: gastado as Centavos, presupuesto: presupuesto as Centavos, parteBp: Math.round((gastado * 10_000) / presupuesto) };
    })
    .sort((a, b) => b.parteBp - a.parteBp || a.codigo.localeCompare(b.codigo));
  const sin = gastos.filter((l) => !(l.codigo in presupuestos) && l.monto !== 0);
  return {
    filas,
    gastado: filas.reduce((a, f) => a + f.gastado, 0) as Centavos,
    presupuesto: filas.reduce((a, f) => a + f.presupuesto, 0) as Centavos,
    sinPresupuesto: { monto: sin.reduce((a, l) => a + l.monto, 0) as Centavos, cuentas: sin.length },
  };
}

// ---------------------------------------------------------------------------
// Lo que se pierde
// ---------------------------------------------------------------------------

/** The period's losses, by the account each went to, the largest first. */
export function perdidasDelPeriodo(gastos: readonly LineaReporte[]): { lineas: LineaReporte[]; total: Centavos } {
  const lineas = gastos.filter((l) => CUENTAS_DE_PERDIDA.includes(l.codigo) && l.monto > 0).sort((a, b) => b.monto - a.monto);
  return { lineas, total: lineas.reduce((a, l) => a + l.monto, 0) as Centavos };
}

// ---------------------------------------------------------------------------
// La planilla
// ---------------------------------------------------------------------------

/** What the payroll cost in the period, and what share of the sales and of the gross profit that is. */
export function planillaDelPeriodo(e: Pick<EstadoResultados, 'gastos' | 'totalIngresos' | 'utilidadBruta'>): {
  monto: Centavos;
  deVentasBp: number | null;
  deUtilidadBp: number | null;
} {
  const monto = e.gastos.filter((l) => CUENTAS_DE_PLANILLA.includes(l.codigo)).reduce((a, l) => a + l.monto, 0);
  return {
    monto: monto as Centavos,
    deVentasBp: e.totalIngresos > 0 ? Math.round((monto * 10_000) / e.totalIngresos) : null,
    deUtilidadBp: e.utilidadBruta > 0 ? Math.round((monto * 10_000) / e.utilidadBruta) : null,
  };
}

// ---------------------------------------------------------------------------
// Horas de caja
// ---------------------------------------------------------------------------

/** A till left open is taken as open this long at most: the night it was forgotten is not a shop day. */
export const HORAS_DE_TURNO = 18;

export interface HoraDeCaja {
  /** 0 Sunday to 6 Saturday, and the hour, in Nicaragua. */
  dia: number;
  hora: number;
  /** How many times the till was open at that hour of that day of the week. */
  abiertas: number;
  ventas: number;
  /** Sales an hour, when it was open. */
  porHora: number;
}

const DIA: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const partes = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA_HORARIA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
  hour: 'numeric',
  hourCycle: 'h23',
});

/** An instant's day, day of the week and hour at the shop. */
function enLaTienda(instante: Date): { fecha: string; dia: number; hora: number } {
  const p = partes.formatToParts(instante);
  const v = (tipo: Intl.DateTimeFormatPartTypes) => p.find((x) => x.type === tipo)?.value ?? '';
  return { fecha: `${v('year')}-${v('month')}-${v('day')}`, dia: DIA[v('weekday')] ?? 0, hora: Number(v('hour')) };
}

/**
 * The hours the till was open against the sales rung in them: each hour of each day of the period
 * some till was open (several open at once count once), and for each hour of the week how many
 * times it was open and how many sales it rang an hour. Null when the till was never open.
 */
export function horasDeCaja(d: {
  sesiones: readonly Pick<SesionCaja, 'abiertaEn' | 'cerradaEn'>[];
  /** The instants the period's sales were rung (`momentosDeVentas`). */
  momentos: readonly string[];
  tramo: Tramo;
  /** Now, for a till still open. */
  ahora: string;
}): { abiertas: number; sinVenta: number; celdas: HoraDeCaja[] } | null {
  const HORA = 3_600_000;
  const abiertas = new Map<string, { dia: number; hora: number }>();
  for (const s of d.sesiones) {
    const desde = Date.parse(s.abiertaEn);
    const hasta = Math.min(Date.parse(s.cerradaEn ?? d.ahora), desde + HORAS_DE_TURNO * HORA);
    // Every hour it touched, from the one it opened in.
    for (let t = Math.floor(desde / HORA) * HORA; t < hasta; t += HORA) {
      const x = enLaTienda(new Date(t));
      if (x.fecha < d.tramo.desde || x.fecha > d.tramo.hasta) continue;
      abiertas.set(`${x.fecha}|${x.hora}`, { dia: x.dia, hora: x.hora });
    }
  }
  if (abiertas.size === 0) return null;
  const ventas = new Map<string, number>();
  for (const m of d.momentos) {
    const x = enLaTienda(new Date(m));
    ventas.set(`${x.fecha}|${x.hora}`, (ventas.get(`${x.fecha}|${x.hora}`) ?? 0) + 1);
  }
  const celdas = new Map<string, HoraDeCaja>();
  let sinVenta = 0;
  for (const [clave, x] of abiertas) {
    const n = ventas.get(clave) ?? 0;
    if (n === 0) sinVenta += 1;
    const c = celdas.get(`${x.dia}|${x.hora}`) ?? { dia: x.dia, hora: x.hora, abiertas: 0, ventas: 0, porHora: 0 };
    c.abiertas += 1;
    c.ventas += n;
    celdas.set(`${x.dia}|${x.hora}`, c);
  }
  return {
    abiertas: abiertas.size,
    sinVenta,
    celdas: [...celdas.values()].map((c) => ({ ...c, porHora: c.ventas / c.abiertas })).sort((a, b) => a.dia - b.dia || a.hora - b.hora),
  };
}

/** The busiest hours of the week the till was open, and the slowest of those it was open twice or more. */
export function horasExtremas(celdas: readonly HoraDeCaja[], cuantas = 3): { movidas: HoraDeCaja[]; flojas: HoraDeCaja[] } {
  const movidas = [...celdas].filter((c) => c.ventas > 0).sort((a, b) => b.porHora - a.porHora || a.dia - b.dia || a.hora - b.hora).slice(0, cuantas);
  const flojas = [...celdas]
    .filter((c) => c.abiertas >= 2 && !movidas.includes(c))
    .sort((a, b) => a.porHora - b.porHora || a.dia - b.dia || a.hora - b.hora)
    .slice(0, cuantas);
  return { movidas, flojas };
}

// ---------------------------------------------------------------------------
// ¿Me alcanza?
// ---------------------------------------------------------------------------

export interface CostoDePersona {
  sueldo: Centavos;
  /** INSS patronal and INATEC. */
  patronal: Centavos;
  /** The aguinaldo and the vacations it accrues, a twelfth of the wage each. */
  prestaciones: Centavos;
  total: Centavos;
}

/**
 * What a person costs the shop a month at a monthly wage, as the payroll counts them: the total is
 * the one a new person's sheet says («Le cuesta a la tienda», `costoDePago`), here in its parts.
 */
export function costoDeContratar(sueldo: Centavos, rates: PayrollRates, opciones: { conInss: boolean; conPrestaciones: boolean }): CostoDePersona {
  const r = calcularPlanilla({ bruto: sueldo }, rates, { pagosPorAnio: 12, ...opciones });
  return {
    sueldo,
    patronal: r.costoPatronal,
    prestaciones: (r.provisionAguinaldo + r.provisionVacaciones) as Centavos,
    total: costoDePago(sueldo, opciones, rates),
  };
}

export interface Alcanza {
  /** The gross margin the extra sales would leave, in basis points; null when nothing sold at one. */
  margenBp: number | null;
  /** What is left a month now, after every expense, and what would be with this one. */
  quedaAlMes: Centavos;
  quedaria: Centavos;
  /** What more would have to sell a month to pay for it, and a day. */
  venderAlMes: Centavos | null;
  venderAlDia: Centavos | null;
}

/**
 * Whether a new monthly cost fits: what is left a month after every expense, at what the last days
 * left, with and without it, and what more would have to sell to pay for it at the margin the
 * shop makes.
 */
export function alcanza(d: { mensual: number; ingreso: number; utilidadBruta: number; utilidadNeta: number; dias: number }): Alcanza {
  const alMes = 30 / Math.max(1, d.dias);
  const queda = Math.round(d.utilidadNeta * alMes);
  const conMargen = d.ingreso > 0 && d.utilidadBruta > 0;
  // Over the profit, not over a margin worked out first: C$ 1,500 at 30% is C$ 5,000, not C$ 5,000.01.
  const vender = conMargen ? Math.ceil((d.mensual * d.ingreso) / d.utilidadBruta) : null;
  return {
    margenBp: conMargen ? Math.round((d.utilidadBruta * 10_000) / d.ingreso) : null,
    quedaAlMes: queda as Centavos,
    quedaria: (queda - d.mensual) as Centavos,
    venderAlMes: vender as Centavos | null,
    venderAlDia: vender === null ? null : (Math.ceil(vender / 30) as Centavos),
  };
}
